/*
 * Cart drawer behaviour.
 *
 * Owns opening/closing, cart mutations and re-rendering. Re-render goes
 * through Shopify's Section Rendering API and swaps the single
 * [data-gy-inner] node, so every price, discount and total stays rendered by
 * Liquid from Shopify's cart object — nothing about money is recomputed here.
 *
 * Every listener is delegated from the drawer root, so swapping that node
 * never detaches a handler.
 *
 * This theme (Molding) ships its own jQuery mini cart on `.js-mini-cart`,
 * opened by adding the class "active". That drawer is hidden by CSS and its
 * toggle is intercepted in the capture phase below, so the header cart button
 * opens this drawer instead. The header badge `.js-cart-count` is still the
 * theme's, so it is kept in sync by hand.
 */
(function () {
  'use strict';

  var root = null;
  var sectionId = '';
  var busy = false;

  function routes() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ---------------- open / close ---------------- */

  function open() {
    if (!root) return;
    // Shopflo: cart-cta-bridge.js reveals the cart on every successful /cart/add —
    // including the internal cart-sync call the Shopflo bundle makes while
    // processing Buy Now. Skip opening anything while that flag is set, so
    // Buy Now doesn't get its own checkout overlay pre-empted by the cart.
    if (window.__floSuppressCartOpen) return;
    if (typeof handleFloCartBtn === 'function') { handleFloCartBtn(); return; } // Shopflo: intercept cart open

    // === Original open() body — commented out by Shopflo integration ===
    // root.classList.add('is-open');
    // document.body.classList.add('gy-cart-open');
    // var closeBtn = root.querySelector('[data-gy-close]');
    // if (closeBtn) closeBtn.focus({ preventScroll: true });
    // === end original body ===
  }

  function close() {
    if (!root) return;
    root.classList.remove('is-open');
    document.body.classList.remove('gy-cart-open');
  }

  function isOpen() {
    return !!root && root.classList.contains('is-open');
  }

  /* ---------------- rendering ---------------- */

  function setBusy(state) {
    busy = state;
    var inner = root && root.querySelector('[data-gy-inner]');
    if (inner) inner.classList.toggle('gy-busy', state);
  }

  /* Pull the section back through the Section Rendering API and swap the one
     cart-dependent node.
     An add fires two signals — the cart bridge's fetch hook and the section's
     own "cart:refresh" event — so collapse near-simultaneous calls into the
     one already in flight rather than rendering the section twice. */
  var inFlight = null;
  var lastRefreshAt = 0;

  function refresh(force) {
    if (!root || !sectionId) return Promise.resolve();
    if (inFlight) return inFlight;
    /* Passive signals debounce; an actual mutation must always re-render. */
    if (!force && Date.now() - lastRefreshAt < 350) return Promise.resolve();
    inFlight = doRefresh().then(function (v) {
      inFlight = null;
      lastRefreshAt = Date.now();
      return v;
    }, function (e) {
      inFlight = null;
      lastRefreshAt = Date.now();
      throw e;
    });
    return inFlight;
  }

  function doRefresh() {
    var url = routes() + '?sections=' + encodeURIComponent(sectionId);
    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data[sectionId];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-gy-inner]');
        var current = root.querySelector('[data-gy-inner]');
        if (fresh && current) {
          current.parentNode.replaceChild(fresh, current);
          syncBadge(fresh);
        }
      })
      .catch(function () { /* leave the current markup in place */ });
  }

  /* The header badge belongs to the theme, so mirror the count the section
     just rendered into it rather than firing another /cart.js. */
  function syncBadge(inner) {
    var count = inner && inner.getAttribute('data-gy-count');
    if (count === null || count === undefined) return;
    var nodes = document.querySelectorAll('.js-cart-count');
    for (var i = 0; i < nodes.length; i++) nodes[i].textContent = count;
  }

  function openAndRefresh() {
    open();
    return refresh();
  }

  /* ---------------- cart mutations ---------------- */

  function post(path, body) {
    if (busy) return Promise.resolve();
    setBusy(true);
    return fetch(routes() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      })
      .then(function () { return refresh(true); })
      .catch(function (err) {
        var msg = (err && err.description) || (err && err.message);
        if (msg) window.alert(msg);
      })
      .then(function () { setBusy(false); });
  }

  function changeLine(key, quantity) {
    return post('cart/change.js', { id: key, quantity: quantity });
  }

  function addVariant(id) {
    return post('cart/add.js', { items: [{ id: Number(id), quantity: 1 }] });
  }

  /* ---------------- events ---------------- */

  function lineKeyOf(el) {
    var li = el.closest('.gy-item');
    return li ? li.getAttribute('data-gy-key') : null;
  }

  function qtyInputOf(el) {
    var li = el.closest('.gy-item');
    return li ? li.querySelector('[data-gy-qty]') : null;
  }

  function bind() {
    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      if (target.closest('[data-gy-close]') || target.classList.contains('gy-overlay')) {
        event.preventDefault();
        close();
        return;
      }

      var addBtn = target.closest('[data-gy-add]');
      if (addBtn) {
        event.preventDefault();
        addVariant(addBtn.getAttribute('data-gy-add'));
        return;
      }

      var minus = target.closest('[data-gy-minus]');
      if (minus) {
        var mInput = qtyInputOf(minus);
        var mKey = lineKeyOf(minus);
        if (mInput && mKey) changeLine(mKey, Math.max(0, parseInt(mInput.value, 10) - 1));
        return;
      }

      var plus = target.closest('[data-gy-plus]');
      if (plus) {
        var pInput = qtyInputOf(plus);
        var pKey = lineKeyOf(plus);
        if (pInput && pKey) changeLine(pKey, parseInt(pInput.value, 10) + 1);
        return;
      }

      var remove = target.closest('[data-gy-remove]');
      if (remove) {
        var rKey = lineKeyOf(remove);
        if (rKey) changeLine(rKey, 0);
        return;
      }
    });

    root.addEventListener('change', function (event) {
      var input = event.target.closest && event.target.closest('[data-gy-qty]');
      if (input) {
        var key = lineKeyOf(input);
        var n = parseInt(input.value, 10);
        if (key && !isNaN(n) && n >= 0) changeLine(key, n);
        return;
      }
      var terms = event.target.closest && event.target.closest('[data-gy-terms]');
      if (terms) {
        var checkout = root.querySelector('[data-gy-checkout]');
        if (checkout) checkout.classList.toggle('is-disabled', !terms.checked);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen()) close();
    });
  }

  /* The theme's own cart toggle. Captured on document so this runs before
     Molding's delegated jQuery handler, which would otherwise open its
     mini cart underneath. */
  function interceptThemeCart() {
    document.addEventListener('click', function (event) {
      var t = event.target;
      if (!t || !t.closest) return;
      if (!t.closest('.js-toggle-cart') && !t.closest('[data-gy-open]')) return;
      if (t.closest('#gy-cart')) return;

      event.preventDefault();
      event.stopPropagation();

      var mini = document.querySelector('.js-mini-cart');
      if (mini) mini.classList.remove('active');
      openAndRefresh();
    }, true);
  }

  /* ---------------- boot ---------------- */

  function init() {
    root = document.getElementById('gy-cart');
    if (!root) return;
    sectionId = root.getAttribute('data-section-id') || '';
    bind();
    interceptThemeCart();

    /* Fallback for anything that announces an add without going through the
       cart bridge (which calls openAndRefresh directly and sooner). */
    document.addEventListener('cart:refresh', function () {
      if (root.getAttribute('data-gy-autoopen') === 'true') openAndRefresh();
      else refresh();
    });

    window.GYCart = {
      open: open,
      close: close,
      refresh: refresh,
      openAndRefresh: openAndRefresh
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
